import BackButtonTrap from "../components/shared/BackButtonTrap";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mealsApi, pantryApi, peopleApi, sharedApi, sharingApi, shoppingApi } from "../services/api";
import SavingNotice from "../components/shared/SavingNotice";
import ConfirmModal from "../components/shared/ConfirmModal";
import { SheetLogoRow } from "../components/shared/SheetChrome";
import useMinLoad from "../hooks/useMinLoad";
import { useToast } from "../components/shared/Toast";
import { useAuth } from "../hooks/useAuth";

function valueText(value) {
  if (value === 0) return "0";
  if (!value) return "";
  return String(value);
}

function DetailLine({ label, value }) {
  const text = valueText(value);
  if (!text) return null;
  return (
    <div className="hm-shared-view-line">
      <span>{label}</span>
      <strong>{text}</strong>
    </div>
  );
}

function DetailList({ title, items, renderItem, ordered = false }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!list.length) return null;
  const Tag = ordered ? "ol" : "div";
  return (
    <section className="hm-shared-view-section">
      <h2>{title}</h2>
      <Tag className="hm-shared-view-list">
        {list.map((item, index) => {
          const content = renderItem ? renderItem(item, index) : String(item);
          return ordered ? <li key={item?.id || index}>{content}</li> : <span key={item?.id || index}>{content}</span>;
        })}
      </Tag>
    </section>
  );
}

function tagsFrom(...values) {
  return values.flatMap(value => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    return String(value).split(",").map(item => item.trim()).filter(Boolean);
  });
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addDays(date, days) {
  if (!date) return null;
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function fullDate(date) {
  if (!date) return "";
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function mealPlanRangeLabel(weekStartValue) {
  const weekStart = parseDate(weekStartValue);
  const rangeEnd = addDays(weekStart, 6);
  return weekStart && rangeEnd ? `${fullDate(weekStart)} - ${fullDate(rangeEnd)}` : weekStartValue || "";
}

function ownerFirstName(name) {
  return String(name || "Family member").trim().split(/\s+/)[0] || "Family member";
}

function ownerInitial(name) {
  return ownerFirstName(name).charAt(0).toUpperCase() || "F";
}

function ownerShareNote(type, detail = {}) {
  const notes = {
    meal_plan: {
      title: "This is your shared preview",
      body: "Family members see this read-only plan and can copy it into their own account. Your original meal plan stays unchanged.",
      button: "Back to shared items",
    },
    person: {
      title: "This person profile is shared",
      body: "Family members can view the profile details you shared. Gift history, private ideas, and anything not included in this share stay in your account.",
      button: "Back to shared people",
    },
    pantry: {
      title: "This pantry snapshot is shared",
      body: "Family members can view these pantry items and add them to their own pantry. Your pantry stays unchanged.",
      button: "Back to shared food",
    },
    trip: {
      title: "This trip plan is shared",
      body: "Family members can view the trip details, packing notes, and activities you shared. Your original plan stays unchanged.",
      button: "Back to shared trips",
    },
    event: {
      title: "This event is shared",
      body: "Family members can view the event details you shared. Your original event, RSVPs, and private planning notes stay in your account.",
      button: "Back to shared events",
    },
  };
  if (type === "occasion" && detail?.occasion_type === "trip") return notes.trip;
  return notes[type] || null;
}

function mealTitleParts(day) {
  const recipe = day?.recipe || {};
  const rawTitle = recipe.title || day?.meal_name || "";
  const cuisineMatch = String(rawTitle).match(/\s+\(([^)]+)\)\s*$/);
  const title = cuisineMatch ? String(rawTitle).replace(/\s+\([^)]+\)\s*$/, "").trim() : rawTitle;
  const sub = [recipe.cuisine || cuisineMatch?.[1], recipe.difficulty].filter(Boolean).join(" · ");
  const time = day?.cook_time || (day?.prep_time_minutes ? `${day.prep_time_minutes} min` : "");
  return { title, sub, time };
}

function sharedMealKey(day) {
  return [
    Number(day?.day_index || 0),
    String(day?.meal_type || "dinner").trim().toLowerCase(),
    String(mealTitleParts(day).title || day?.meal_name || "").trim().toLowerCase(),
  ].join("|");
}

const SHARED_RECIPE_IMAGE_FALLBACKS = [
  { keys: ["taco", "quesadilla", "tortilla"], urls: [
    "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?w=420&h=300&fit=crop",
  ] },
  { keys: ["pasta", "spaghetti", "noodle"], urls: [
    "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1621996346565-e3dbc646d9a9?w=420&h=300&fit=crop",
  ] },
  { keys: ["rice", "bowl", "grain"], urls: [
    "https://images.unsplash.com/photo-1512058564366-18510be2db19?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=420&h=300&fit=crop",
  ] },
  { keys: ["salad", "greens", "spinach"], urls: [
    "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1540420773420-3366772f4999?w=420&h=300&fit=crop",
  ] },
  { keys: ["soup", "stew"], urls: [
    "https://images.unsplash.com/photo-1547592166-23ac45744acd?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1604152135912-04a022e23696?w=420&h=300&fit=crop",
  ] },
  { keys: ["chicken", "skillet"], urls: [
    "https://images.unsplash.com/photo-1598515214211-89d3c73ae83b?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1532550907401-a500c9a57435?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?w=420&h=300&fit=crop",
  ] },
  { keys: ["bread", "toast", "sandwich"], urls: [
    "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=420&h=300&fit=crop",
  ] },
  { keys: ["stir", "vegetable", "veggie"], urls: [
    "https://images.unsplash.com/photo-1512058564366-18510be2db19?w=420&h=300&fit=crop",
    "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=420&h=300&fit=crop",
  ] },
];

const DEFAULT_SHARED_RECIPE_IMAGE = "https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=420&h=300&fit=crop";

function stableImageIndex(value, length) {
  if (!length) return 0;
  let hash = 0;
  for (const char of String(value || "")) {
    hash = ((hash << 5) - hash + char.charCodeAt(0)) | 0;
  }
  return Math.abs(hash) % length;
}

function sharedRecipeFallbackImageUrl(day) {
  const recipe = day?.recipe || {};
  const haystack = [
    recipe.title,
    day?.meal_name,
    ...(recipe.ingredients || []).map(ing => typeof ing === "string" ? ing : ing?.name || ing?.item),
  ].join(" ").toLowerCase();
  const matched = SHARED_RECIPE_IMAGE_FALLBACKS.find(item => item.keys.some(key => haystack.includes(key)));
  if (!matched) return DEFAULT_SHARED_RECIPE_IMAGE;
  return matched.urls[stableImageIndex(haystack, matched.urls.length)] || DEFAULT_SHARED_RECIPE_IMAGE;
}

function sharedMealImageUrl(day) {
  return day?.photo_url || day?.image_url || day?.recipe?.photo_url || day?.recipe?.image_url || sharedRecipeFallbackImageUrl(day);
}

function ReadOnlyNotice() {
  return (
    <div className="hm-shared-view-note">
      Read-only shared view. Changes stay in the owner's account.
    </div>
  );
}

function SharedActionNotice({ type }) {
  const label = type === "recipe"
    ? "This shared recipe stays here. Cooking it only updates your pantry."
    : "This shared item is not saved automatically. Choose an action when you are ready.";
  return (
    <div className="hm-shared-view-note hm-shared-view-note--action">
      {label}
    </div>
  );
}

function sharedBadgeText(type, isOwnerViewer, hasAction) {
  if (isOwnerViewer) return "Shared by you";
  if (!hasAction) return "View only";
  if (type === "recipe") return "Can cook";
  if (type === "meal_plan") return "Can use";
  if (type === "person") return "Can save";
  if (type === "pantry" || type === "shopping_list") return "Can add";
  if (type === "pet") return hasAction ? "Help with care" : "View only";
  return "Action available";
}

function isOwnSharedItemError(err) {
  return err?.status === 403 && /already own this shared item/i.test(err?.message || err?.data?.error || "");
}

function SharedMealPlanView({ detail, days, ownerName, onMealTap }) {
  const weekStart = parseDate(detail.week_start);
  const sortedDays = [...days].sort((a, b) => (a.day_index ?? 0) - (b.day_index ?? 0));
  const planRange = mealPlanRangeLabel(detail.week_start);

  return (
    <section className="hm-shared-meal-plan">
      <div className="fd-plan-header hm-shared-plan-header">
        <div className="fd-plan-summary">
          <h3 className="fd-plan-title">This week's plan</h3>
          <p className="fd-plan-sub">{sortedDays.length || 0} days{planRange ? ` · ${planRange}` : ""}{ownerName ? ` · ${ownerFirstName(ownerName)}'s plan` : ""}</p>
        </div>
      </div>

      <div className="fd-week-grid hm-shared-week-grid">
        {sortedDays.map(day => {
          const date = addDays(weekStart, day.day_index || 0);
          const parts = mealTitleParts(day);
          const hasMeal = Boolean(parts.title);
          const imageUrl = hasMeal ? sharedMealImageUrl(day) : "";
          return (
            <article
              key={day.id || day.day_index}
              role={hasMeal ? "button" : undefined}
              tabIndex={hasMeal ? 0 : undefined}
              className={`fd-day-card hm-shared-day-card${hasMeal ? " fd-day-card--button" : ""}${day.uses_expiring_items ? " fd-day-card--expiring" : ""}${!hasMeal ? " fd-day-card--empty" : ""}`}
              onClick={() => {
                if (!hasMeal) return;
                onMealTap?.(day);
              }}
              onContextMenu={event => event.preventDefault()}
              onKeyDown={event => {
                if (!hasMeal || !["Enter", " "].includes(event.key)) return;
                event.preventDefault();
                onMealTap?.(day);
              }}
            >
              <div className="fd-day-date">
                <span className="fd-day-label">{(day.day_label || "Day").slice(0, 3)}</span>
                <span className="fd-day-date__sub">{fullDate(date)}</span>
              </div>
              <div className="fd-day-photo">
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt=""
                    loading="lazy"
                    data-fallback-src={sharedRecipeFallbackImageUrl(day)}
                    onError={event => {
                      const fallback = event.currentTarget.dataset.fallbackSrc;
                      if (fallback && event.currentTarget.src !== fallback) {
                        event.currentTarget.src = fallback;
                      }
                    }}
                  />
                )}
                <span aria-hidden="true">🍽</span>
              </div>
              <div className="fd-day-main">
                <div className="fd-day-meal">
                  {parts.title || <em className="fd-muted">Empty</em>}
                  {parts.sub && <span>{parts.sub}</span>}
                </div>
                <div className="fd-day-meta">
                  {day.meal_type && <span className="fd-cuisine-chip">{day.meal_type}</span>}
                  {parts.time && <div className="fd-day-time">⏱ {parts.time}</div>}
                </div>
              </div>
              {day.notes && <div className="fd-day-notes">{day.notes}</div>}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function sharedItemKey(item, index) {
  return String(item?.id ?? `${item?.name || "item"}-${item?.quantity || ""}-${item?.expiry_date || ""}-${index}`);
}

function SharedPantryItemsView({ items = [], selectedKeys = [], selectionMode = false, onItemTap, onItemLongPress }) {
  const longPressRef = useRef({ timer: null, fired: false });
  const selected = new Set(selectedKeys);

  function clearLongPressTimer() {
    if (longPressRef.current.timer) {
      window.clearTimeout(longPressRef.current.timer);
      longPressRef.current.timer = null;
    }
  }

  return (
    <section className="hm-shared-view-section hm-shared-pantry-section">
      <h2>Pantry</h2>
      <div className="hm-shared-pantry-list">
        {items.map((item, index) => {
          const key = sharedItemKey(item, index);
          const isSelected = selected.has(key);
          const label = `${item.name}${item.quantity ? ` · ${item.quantity}` : ""}${item.expiry_date ? ` · expires ${item.expiry_date}` : ""}`;
          return (
            <button
              type="button"
              key={key}
              className={`hm-shared-pantry-item${isSelected ? " is-selected" : ""}${selectionMode ? " is-selectable" : ""}`}
              aria-pressed={selectionMode ? isSelected : undefined}
              onClick={() => {
                if (longPressRef.current.fired) {
                  longPressRef.current.fired = false;
                  return;
                }
                onItemTap?.(item, index);
              }}
              onPointerDown={() => {
                clearLongPressTimer();
                longPressRef.current.fired = false;
                longPressRef.current.timer = window.setTimeout(() => {
                  longPressRef.current.fired = true;
                  onItemLongPress?.(item, index);
                }, 450);
              }}
              onPointerUp={clearLongPressTimer}
              onPointerCancel={clearLongPressTimer}
              onPointerLeave={clearLongPressTimer}
            >
              <span className="hm-shared-pantry-check">{isSelected ? "✓" : ""}</span>
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function mealListItem(day) {
  const parts = mealTitleParts(day);
  return {
    day_index: day?.day_index,
    day_label: day?.day_label || "Day",
    meal_name: parts.title || day?.meal_name || "Meal",
  };
}

function successMealsForMode(mode, sharedDays, decision, extra = {}, result = {}) {
  if (Array.isArray(result?.saved_day_indices)) {
    const saved = new Set(result.saved_day_indices.map(Number));
    return sharedDays
      .filter(day => saved.has(Number(day?.day_index)))
      .map(mealListItem);
  }
  if (Array.isArray(result?.copied_day_indices)) {
    const copied = new Set(result.copied_day_indices.map(Number));
    return sharedDays
      .filter(day => copied.has(Number(day?.day_index)))
      .map(mealListItem);
  }
  if (mode === "fill_empty" && decision?.days) {
    return decision.days
      .filter(day => !day.is_filled && day.shared)
      .map(day => mealListItem(day.shared));
  }
  if (mode === "choose") {
    const chosenKeys = new Set(extra.use_shared_meals || []);
    const chosen = new Set(extra.use_shared_days || []);
    return sharedDays
      .filter(day => chosenKeys.size ? chosenKeys.has(sharedMealKey(day)) : chosen.has(day.day_index))
      .map(mealListItem);
  }
  return sharedDays.filter(day => mealTitleParts(day).title).map(mealListItem);
}

function SharedMealPlanSuccess({ success, onOpenMealPlan, onBack }) {
  const count = success?.count ?? success?.meals?.length ?? 0;
  const savedAsRecipes = success?.mode === "save_recipes";
  return (
    <section className="hm-shared-success" aria-live="polite">
      <div className="hm-shared-success-icon">✓</div>
      <h2>{`${count} meal${count === 1 ? "" : "s"} ${savedAsRecipes ? "saved as recipes" : "added to your plan"}`}</h2>
      {success?.message && <p>{success.message}</p>}
      <div className="hm-shared-success-list">
        {(success?.meals || []).map((meal, index) => (
          <span key={`${meal.day_label}-${meal.meal_name}-${index}`}>
            {meal.day_label} · {meal.meal_name}
          </span>
        ))}
      </div>
      <button type="button" className="hm-shared-footer-primary" onClick={onOpenMealPlan}>
        {savedAsRecipes ? "Open recipes" : "Open my meal plan"}
      </button>
      <button type="button" className="hm-shared-footer-link" onClick={onBack}>
        Back to family sharing
      </button>
    </section>
  );
}

function SharedRecipeCookSheet({ recipe, ingredients, instructions, saving, onCook, onClose }) {
  return (
    <div className="fd-recipe-sheet-backdrop" onClick={onClose}>
      <div
        className="fd-recipe-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`${recipe?.title || "Shared recipe"} steps`}
        onClick={event => event.stopPropagation()}
      >
        <button type="button" className="hm-sheet-handle" onClick={onClose} aria-label="Close recipe" />
        <button type="button" className="fd-recipe-sheet__close" onClick={onClose} aria-label="Close recipe">×</button>
        {(recipe?.image_url || recipe?.photo_url) && (
          <div className="fd-recipe-sheet__photo">
            <img src={recipe.image_url || recipe.photo_url} alt="" />
          </div>
        )}
        <div className="fd-recipe-sheet__head">
          <h3>{recipe?.title || "Shared recipe"}</h3>
          <p>
            {recipe?.prep_time || recipe?.cook_time ? `${(recipe?.prep_time || 0) + (recipe?.cook_time || 0)} min` : null}
            {recipe?.difficulty ? `${recipe?.prep_time || recipe?.cook_time ? " · " : ""}${recipe.difficulty}` : null}
          </p>
        </div>
        <div className="fd-recipe-sheet__body">
          {ingredients.length > 0 && (
            <div className="fd-recipe-section">
              <p className="fd-label">Ingredients</p>
              <div className="fd-tag-row">
                {ingredients.map((item, index) => <span key={index} className="fd-tag fd-tag--green">{item}</span>)}
              </div>
            </div>
          )}
          {instructions.length > 0 && (
            <div className="fd-recipe-section">
              <p className="fd-label">Steps</p>
              <ol className="fd-steps-list fd-steps-list--sheet">
                {instructions.map((step, index) => <li key={index}>{step}</li>)}
              </ol>
            </div>
          )}
        </div>
        <div className="fd-recipe-sheet__actions">
          <button className="hm-btn hm-btn--primary" type="button" onClick={onCook} disabled={saving}>
            {saving ? "Cooking..." : "✓ Cooked it - mark pantry used"}
          </button>
        </div>
      </div>
    </div>
  );
}

function itemName(item) {
  return (item?.name || item?.item || item?.title || "").trim();
}

function sharedItemsForPantry(items = []) {
  return items.map(item => ({
    name: itemName(item),
  })).filter(item => item.name);
}

function sharedItemsForShopping(items = []) {
  return items.map(item => ({
    name: itemName(item),
    section: item.section || item.category || "other",
    quantity: item.quantity,
    source: "shared",
  })).filter(item => item.name);
}

function SharedItemsDestinationSheet({ type, ownerName, saving, onAddPantry, onAddShopping, onClose }) {
  const isPantry = type === "pantry";
  return (
    <div className="hm-shared-sheet-overlay" role="presentation" onClick={onClose}>
      <BackButtonTrap onClose={onClose} />
      <section className="hm-shared-decision-sheet" role="dialog" aria-modal="true" aria-label="Choose where to add shared items" onClick={event => event.stopPropagation()}>
        <button type="button" className="hm-shared-decision-handle" onClick={onClose} aria-label="Close add options" />
        <button type="button" className="hm-shared-decision-close" onClick={onClose} aria-label="Close add options">×</button>
        <h2>Where should these go?</h2>
        <p>{ownerFirstName(ownerName)}'s item dates stay read-only. Expiry dates will not be copied into your account.</p>
        <button type="button" className="hm-shared-decision-option" onClick={onAddPantry} disabled={saving}>
          <span className="hm-shared-decision-icon">＋</span>
          <span>
            <strong>Add to my pantry</strong>
            <small>{isPantry ? "Adds the items you now have. Existing items increase quantity." : "Use this when the list represents items you already bought."}</small>
          </span>
          <span aria-hidden="true">›</span>
        </button>
        <button type="button" className="hm-shared-decision-option" onClick={onAddShopping} disabled={saving}>
          <span className="hm-shared-decision-icon">☷</span>
          <span>
            <strong>Add to my shopping list</strong>
            <small>{isPantry ? "Use this as a buy-later list from the shared pantry." : "Copies these items into your open shopping list."}</small>
          </span>
          <span aria-hidden="true">›</span>
        </button>
        <button type="button" className="hm-shared-decision-quiet" onClick={onClose}>
          Cancel
        </button>
      </section>
    </div>
  );
}

export default function SharedContentViewer() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [mealPlanDecision, setMealPlanDecision] = useState(null);
  const [choosingDays, setChoosingDays] = useState(false);
  const [selectedDays, setSelectedDays] = useState({});
  const [mealActionSheet, setMealActionSheet] = useState(null);
  const [selectedSharedPantry, setSelectedSharedPantry] = useState({});
  const [hiddenSharedPantry, setHiddenSharedPantry] = useState({});
  const [recipeSheetOpen, setRecipeSheetOpen] = useState(false);
  const [itemsDestinationOpen, setItemsDestinationOpen] = useState(false);
  const [ownerCopyWarningOpen, setOwnerCopyWarningOpen] = useState(false);
  const decisionHandleDragRef = useRef({ active: false, startY: 0, moved: false });
  const mealSheetDragRef = useRef({ active: false, startY: 0, offset: 0 });
  const mealSheetCloseTimerRef = useRef(null);
  const [mealSheetOffset, setMealSheetOffset] = useState(0);
  const [mealSheetDragging, setMealSheetDragging] = useState(false);

  useEffect(() => () => {
    if (mealSheetCloseTimerRef.current) {
      window.clearTimeout(mealSheetCloseTimerRef.current);
    }
    mealSheetDragRef.current = { active: false, startY: 0, offset: 0 };
  }, []);
  const [mealPlanSuccess, setMealPlanSuccess] = useState(null);
  const showLoading = useMinLoad(loading, 500);
  const showSaving = useMinLoad(saving, 600);

  function releaseSharedViewLock() {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }

  function showOwnerCopyWarning() {
    setError("");
    setMealActionSheet(null);
    setMealPlanDecision(null);
    setRecipeSheetOpen(false);
    setItemsDestinationOpen(false);
    setOwnerCopyWarningOpen(true);
  }

  function closeOwnerCopyWarning() {
    setOwnerCopyWarningOpen(false);
    releaseSharedViewLock();
  }

  function closeOwnerCopyWarningAndReturn() {
    closeOwnerCopyWarning();
    window.setTimeout(() => navigate("/app/family"), 0);
  }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    sharedApi.get(shareId)
      .then(data => {
        if (!alive) return;
        setPayload(data);
      })
      .catch(err => {
        if (!alive) return;
        setError(err.message || "Could not open this shared item.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [shareId]);

  const share = payload?.share;
  const detail = payload?.detail || {};
  const type = share?.share_type || share?.resource_type;
  const ownerName = share?.owner_name || share?.owner_email || "Family member";
  const isOwnerViewer = Boolean(user?.id && share?.owner_user_id === user.id);
  const ingredients = useMemo(() => Array.isArray(detail.ingredients)
    ? detail.ingredients.map(item => typeof item === "string" ? item : [item?.quantity, item?.unit, item?.name || item?.item].filter(Boolean).join(" "))
    : [], [detail.ingredients]);
  const instructions = Array.isArray(detail.instructions) ? detail.instructions : [];
  const items = Array.isArray(detail.items) ? detail.items : [];
  const visibleSharedPantryItems = type === "pantry"
    ? items.filter((item, index) => !hiddenSharedPantry[sharedItemKey(item, index)])
    : items;
  const selectedSharedPantryKeys = Object.entries(selectedSharedPantry).filter(([, selected]) => selected).map(([key]) => key);
  const sharedPantrySelectionMode = selectedSharedPantryKeys.length > 0;
  const mealDays = Array.isArray(detail.days) ? detail.days : [];
  const mealPlanRange = mealPlanRangeLabel(detail.week_start);
  const occasionItems = Array.isArray(detail.occasions) ? detail.occasions : [];
  const packingItems = Array.isArray(detail.packing_data?.items) ? detail.packing_data.items : [];
  const activityItems = Array.isArray(detail.activities_data?.activities) ? detail.activities_data.activities : [];
  const canHelpWithPetCare = type === "pet" && !isOwnerViewer && share?.permission === "edit";
  const showActionButton = !isOwnerViewer && (["recipe", "meal_plan", "pantry", "shopping_list", "person"].includes(type) || canHelpWithPetCare);
  const preferenceCheck = payload?.preference_check || null;
  const hardConflicts = Array.isArray(preferenceCheck?.hard_conflicts) ? preferenceCheck.hard_conflicts : [];
  const hasMealAllergyConflict = type === "meal_plan" && hardConflicts.length > 0;
  const hasMissingFoodPreferences = type === "meal_plan" && preferenceCheck?.status === "missing_preferences";
  const ownerNote = isOwnerViewer ? ownerShareNote(type, detail) : null;
  const badgeText = sharedBadgeText(type, isOwnerViewer, showActionButton);

  async function toggleSharedPetRoutine(routine) {
    setSaving(true); setError("");
    try {
      await sharingApi.completePetRoutine(share.id, routine.id, !routine.completed);
      setPayload(await sharedApi.get(shareId));
      toast?.(routine.completed ? "Marked not done" : `${routine.title} marked done`, "success");
    } catch (err) { setError(err.message || "That care update could not be saved."); }
    finally { setSaving(false); }
  }

  function allergyDecisionFromPreferenceCheck(check = preferenceCheck) {
    const conflicts = new Map((check?.hard_conflicts || []).map(item => [Number(item.day_index), item]));
    return {
      status: "allergy_conflict",
      filled_count: 0,
      empty_count: (check?.safe_day_indices || []).length,
      preference_check: check,
      days: mealDays.map(day => {
        const index = Number(day?.day_index || 0);
        const conflict = conflicts.get(index);
        return {
          day_index: index,
          day_label: day?.day_label || "Day",
          shared: day,
          mine: null,
          is_filled: false,
          is_unsafe: Boolean(conflict),
          allergy_matches: conflict?.matches || [],
        };
      }),
    };
  }

  function reviewSafeMeals() {
    const decision = allergyDecisionFromPreferenceCheck();
    setMealPlanDecision(decision);
    setChoosingDays(false);
    setSelectedDays(Object.fromEntries((decision.days || []).map(day => [day.day_index, !day.is_unsafe])));
  }

  async function addMealPlan(mode = "auto", extra = {}) {
    setSaving(true);
    setError("");
    try {
      const result = await sharedApi.addMealPlan(shareId, { mode, ...extra });
      const resolvedMode = mode === "auto" ? "replace_empty" : mode;
      const addedMeals = successMealsForMode(resolvedMode, mealDays, mealPlanDecision, extra, result);
      const copiedCount = Number(result?.copied_days);
      const savedRecipeCount = Number(result?.saved_recipes);
      const count = Number.isFinite(savedRecipeCount)
        ? savedRecipeCount
        : Number.isFinite(copiedCount)
          ? copiedCount
          : addedMeals.length;
      const visibleMeals = addedMeals.slice(0, count);
      setMealPlanDecision(null);
      setChoosingDays(false);
      setSaved(true);
      setMealPlanSuccess({
        mode: resolvedMode,
        count,
        meals: visibleMeals,
        message: resolvedMode === "save_recipes"
          ? "Saved to your recipe library without changing your scheduled meal plan."
          : resolvedMode === "fill_empty"
          ? `Your existing meals were kept. ${ownerFirstName(ownerName)}'s meals filled the empty days.`
          : resolvedMode === "choose"
            ? "Your selected days were updated."
            : `${ownerFirstName(ownerName)}'s full plan was added.`
      });
      toast(resolvedMode === "save_recipes" ? "Recipes saved ✓" : "Meal plan added ✓", "success");
    } catch (err) {
      if (err.status === 409 && err.data) {
        setMealPlanDecision(err.data);
        setChoosingDays(false);
        setSelectedDays(Object.fromEntries((err.data.days || []).map(day => [day.day_index, !day.is_filled && !day.is_unsafe])));
      } else if (isOwnSharedItemError(err)) {
        showOwnerCopyWarning();
      } else {
        setError(err.message || "Could not add this meal plan.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function addPantry(selectedItems = null) {
    if (isOwnerViewer) {
      showOwnerCopyWarning();
      return;
    }
    const itemsToAdd = selectedItems || visibleSharedPantryItems;
    setSaving(true);
    setError("");
    try {
      const result = selectedItems
        ? await pantryApi.bulkAdd(sharedItemsForPantry(itemsToAdd))
        : await sharedApi.addPantry(shareId);
      setSaved(true);
      setItemsDestinationOpen(false);
      const total = (result?.added || 0) + (result?.updated || 0);
      toast(`${total || "Pantry"} item${total === 1 ? "" : "s"} added ✓`, "success");
    } catch (err) {
      if (isOwnSharedItemError(err)) {
        showOwnerCopyWarning();
      } else {
        setError(err.message || "Could not add pantry items.");
      }
    } finally {
      setSaving(false);
    }
  }

  function toggleSharedPantrySelection(item, index) {
    const key = sharedItemKey(item, index);
    setSelectedSharedPantry(current => ({ ...current, [key]: !current[key] }));
  }

  function selectedSharedPantryItems() {
    const selected = new Set(selectedSharedPantryKeys);
    return visibleSharedPantryItems.filter((item, index) => selected.has(sharedItemKey(item, index)));
  }

  function clearSharedPantrySelection() {
    setSelectedSharedPantry({});
  }

  async function addSelectedSharedPantry() {
    const selectedItems = selectedSharedPantryItems();
    if (!selectedItems.length) return;
    await addPantry(selectedItems);
    clearSharedPantrySelection();
  }

  function removeSelectedSharedPantryFromView() {
    const selected = selectedSharedPantryKeys;
    if (!selected.length) return;
    setHiddenSharedPantry(current => {
      const next = { ...current };
      selected.forEach(key => { next[key] = true; });
      return next;
    });
    clearSharedPantrySelection();
    toast(`${selected.length} shared pantry item${selected.length === 1 ? "" : "s"} removed from view`, "success");
  }

  async function addShoppingItems() {
    if (isOwnerViewer) {
      showOwnerCopyWarning();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await shoppingApi.bulkAdd(sharedItemsForShopping(items));
      setSaved(true);
      setItemsDestinationOpen(false);
      const total = result?.created_count ?? sharedItemsForShopping(items).length;
      toast(`${total || "Shopping"} item${total === 1 ? "" : "s"} added to shopping list ✓`, "success");
    } catch (err) {
      if (isOwnSharedItemError(err)) {
        showOwnerCopyWarning();
      } else {
        setError(err.message || "Could not add shopping items.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function savePerson() {
    setSaving(true);
    setError("");
    try {
      await peopleApi.copyFromShare(shareId);
      setSaved(true);
      toast(`${detail.name || share?.title || "Person"} added to your People & Gifts ✓`, "success");
    } catch (err) {
      if (isOwnSharedItemError(err)) {
        showOwnerCopyWarning();
      } else {
        setError(err.message || "Could not save this person.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function cookSharedRecipe() {
    setSaving(true);
    setError("");
    try {
      await mealsApi.cookRecipe(detail);
      setSaved(true);
      setRecipeSheetOpen(false);
      toast("Pantry updated ✓", "success");
    } catch (err) {
      if (isOwnSharedItemError(err)) {
        showOwnerCopyWarning();
      } else {
        setError(err.message || "Could not mark this recipe cooked.");
      }
    } finally {
      setSaving(false);
    }
  }

  function primaryActionLabel() {
    if (saved) return "✓ Done";
    if (type === "meal_plan") return hasMealAllergyConflict ? "Review safe meals" : "Use this meal plan";
    if (type === "pantry") return "Add shared items";
    if (type === "shopping_list") return "Add shared items";
    if (type === "person") return "Save to my people";
    if (type === "recipe") return "Cook this →";
    return "";
  }

  function runPrimaryAction() {
    if (saving || saved) return;
    if (type === "meal_plan") {
      if (hasMealAllergyConflict) reviewSafeMeals();
      else addMealPlan();
    } else if (type === "pantry") {
      setItemsDestinationOpen(true);
    } else if (type === "shopping_list") {
      setItemsDestinationOpen(true);
    } else if (type === "person") {
      savePerson();
    } else if (type === "recipe") {
      setRecipeSheetOpen(true);
    }
  }

  function unsafeMealMatches(dayOrKey) {
    const key = typeof dayOrKey === "string" ? dayOrKey : sharedMealKey(dayOrKey);
    const fallbackIndex = typeof dayOrKey === "string" ? Number(String(dayOrKey).split("|")[0]) : Number(dayOrKey?.day_index || 0);
    const conflict = hardConflicts.find(item => item.meal_key ? String(item.meal_key) === key : Number(item.day_index) === fallbackIndex);
    return conflict?.matches || [];
  }

  function selectedMealDayIndices(keys) {
    return keys.map(key => Number(String(key).split("|")[0])).filter(Number.isFinite);
  }

  function openMealActionSheet(day) {
    if (saving || saved || !day) return;
    if (isOwnerViewer) {
      showOwnerCopyWarning();
      return;
    }
    if (mealSheetCloseTimerRef.current) {
      window.clearTimeout(mealSheetCloseTimerRef.current);
      mealSheetCloseTimerRef.current = null;
    }
    setMealSheetOffset(0);
    setMealSheetDragging(false);
    setMealActionSheet({ day });
  }

  function closeMealActionSheet() {
    if (mealSheetCloseTimerRef.current) {
      window.clearTimeout(mealSheetCloseTimerRef.current);
      mealSheetCloseTimerRef.current = null;
    }
    setMealActionSheet(null);
    setMealSheetOffset(0);
    setMealSheetDragging(false);
    mealSheetDragRef.current = { active: false, startY: 0, offset: 0 };
  }

  function closeMealActionSheetWithSlide() {
    setMealSheetDragging(false);
    setMealSheetOffset(window.innerHeight);
    mealSheetCloseTimerRef.current = window.setTimeout(closeMealActionSheet, 140);
  }

  function beginMealSheetDrag(clientY) {
    mealSheetDragRef.current = { active: true, startY: clientY || 0, offset: 0 };
    setMealSheetDragging(true);
  }

  function updateMealSheetDrag(clientY, event) {
    const drag = mealSheetDragRef.current;
    if (!drag.active) return;
    const offset = Math.max(0, (clientY || drag.startY) - drag.startY);
    drag.offset = offset;
    setMealSheetOffset(offset);
    if (offset > 4) event?.preventDefault?.();
  }

  function finishMealSheetDrag() {
    const drag = mealSheetDragRef.current;
    if (!drag.active) return;
    mealSheetDragRef.current = { active: false, startY: 0, offset: 0 };
    if (drag.offset > 42) {
      closeMealActionSheetWithSlide();
      return;
    }
    setMealSheetDragging(false);
    setMealSheetOffset(0);
  }

  function saveMealDaysAsRecipes(mealKeys) {
    const keys = (mealKeys || []).filter(Boolean);
    if (!keys.length) {
      toast("Choose at least one meal first", "error");
      return;
    }
    setMealActionSheet(null);
    addMealPlan("save_recipes", { use_shared_meals: keys, use_shared_days: selectedMealDayIndices(keys) });
  }

  function swapMealDaysIntoPlan(mealKeys) {
    const keys = (mealKeys || []).filter(Boolean);
    if (!keys.length) {
      toast("Choose at least one meal first", "error");
      return;
    }
    setMealActionSheet(null);
    addMealPlan("choose", { use_shared_meals: keys, use_shared_days: selectedMealDayIndices(keys) });
  }

  function toggleSharedDay(index, useShared) {
    setSelectedDays(current => ({ ...current, [index]: useShared }));
  }

  function submitChosenDays() {
    const useSharedDays = Object.entries(selectedDays)
      .filter(([, useShared]) => useShared)
      .map(([index]) => Number(index));
    addMealPlan("choose", { use_shared_days: useSharedDays });
  }

  function closeMealPlanDecision() {
    setMealPlanDecision(null);
    setChoosingDays(false);
    decisionHandleDragRef.current = { active: false, startY: 0, moved: false };
  }

  function startDecisionHandleDrag(event) {
    decisionHandleDragRef.current = { active: true, startY: event.clientY || 0, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function moveDecisionHandleDrag(event) {
    const drag = decisionHandleDragRef.current;
    if (!drag.active) return;
    if ((event.clientY || 0) - drag.startY > 6) {
      drag.moved = true;
    }
  }

  function endDecisionHandleDrag(event) {
    const drag = decisionHandleDragRef.current;
    if (!drag.active) return;
    const deltaY = (event.clientY || drag.startY) - drag.startY;
    decisionHandleDragRef.current = { active: false, startY: 0, moved: false };
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!drag.moved || deltaY > 18) {
      closeMealPlanDecision();
    }
  }

  const sharedTypeClass = type ? ` hm-shared-view--${String(type).replaceAll("_", "-")}` : "";

  return (
    <div className={`hm-shared-view hm-desktop-page-shell${sharedTypeClass}${isOwnerViewer ? " hm-shared-view--owner" : ""}`}>
      {showLoading && <SavingNotice title="Opening" message="Loading shared content..." />}
      {showSaving && <SavingNotice title="Saving" message="Copying to your account..." />}
      {ownerCopyWarningOpen && (
        <ConfirmModal
          tag="Shared by you"
          title="You already own this shared item"
          message="Recipients can copy it into their account. Your original stays in your HouseMind account."
          confirmLabel="Back to Family"
          cancelLabel="Keep previewing"
          onConfirm={closeOwnerCopyWarningAndReturn}
          onCancel={closeOwnerCopyWarning}
          danger
        />
      )}

      <section className="hm-shared-view-topbar">
        <button type="button" className="hm-shared-view-back" onClick={() => navigate("/app/family")}>
          ← Family
        </button>
        <div className="hm-shared-view-titleblock">
          {type !== "meal_plan" && <span>{share?.module_label || "Shared content"}</span>}
          <h1>{type === "meal_plan" && share ? `${ownerFirstName(ownerName)}'s meal plan` : share ? (share.title || detail.title || "Shared item") : "Opening shared item"}</h1>
          {share && <p>Shared by {ownerName}</p>}
        </div>
        {share && <span className="hm-shared-viewonly-badge">{badgeText}</span>}
      </section>

      {error && <div className="hm-family-error">{error}</div>}

      {!loading && !error && share && (
        <>
          {!mealPlanSuccess && (type === "meal_plan" ? (
            <section className="hm-shared-meal-banner">
              <div className="hm-shared-meal-avatar" aria-hidden="true">{ownerInitial(ownerName)}</div>
              <div>
                <strong>Shared by {ownerFirstName(ownerName)}</strong>
                <span>{mealPlanRange}</span>
              </div>
              <span className="hm-shared-viewonly-badge">{badgeText}</span>
            </section>
          ) : (
            type === "pet" && canHelpWithPetCare ? <div className="hm-shared-view-note hm-shared-view-note--action">You can help with today’s care. Your name is recorded when you mark a routine done.</div> : showActionButton ? <SharedActionNotice type={type} /> : <ReadOnlyNotice />
          ))}

          {!mealPlanSuccess && <section className="hm-shared-view-grid">
            {type === "recipe" && (
              <>
                <DetailLine label="Cuisine" value={detail.cuisine} />
                <DetailLine label="Time" value={detail.prep_time || detail.cook_time ? `${(detail.prep_time || 0) + (detail.cook_time || 0)} min` : null} />
                <DetailLine label="Servings" value={detail.servings} />
                <DetailLine label="Difficulty" value={detail.difficulty} />
                <DetailList title="Ingredients" items={ingredients} />
                <DetailList title="Steps" items={instructions} ordered />
              </>
            )}
            {type === "meal_plan" && (
              <>
                <SharedMealPlanView
                  detail={detail}
                  days={mealDays}
                  ownerName={ownerName}
                  onMealTap={openMealActionSheet}
                />
              </>
            )}
            {type === "pantry" && (
              <>
                <DetailLine label="Items" value={visibleSharedPantryItems.length} />
                {sharedPantrySelectionMode && (
                  <div className="hm-shared-pantry-select-bar" role="region" aria-label="Selected shared pantry items">
                    <span>{selectedSharedPantryKeys.length} selected</span>
                    <button type="button" onClick={clearSharedPantrySelection} disabled={saving}>Cancel</button>
                    <button type="button" onClick={addSelectedSharedPantry} disabled={saving}>Add selected</button>
                    <button type="button" className="is-danger" onClick={removeSelectedSharedPantryFromView} disabled={saving}>Remove</button>
                  </div>
                )}
                <SharedPantryItemsView
                  items={visibleSharedPantryItems}
                  selectedKeys={selectedSharedPantryKeys}
                  selectionMode={sharedPantrySelectionMode}
                  onItemTap={(item, index) => {
                    if (sharedPantrySelectionMode) toggleSharedPantrySelection(item, index);
                  }}
                  onItemLongPress={toggleSharedPantrySelection}
                />
              </>
            )}
            {type === "shopping_list" && (
              <>
                <DetailLine label="Store" value={detail.store_name || "Shopping list"} />
                <DetailLine label="Status" value={detail.status} />
                <DetailList title="Items" items={items} renderItem={item => `${item.is_checked ? "✓ " : ""}${item.name}${item.quantity ? ` · ${item.quantity}` : ""}`} />
              </>
            )}
            {type === "person" && (
              <>
                <DetailLine label="Relationship" value={detail.relationship} />
                <DetailList title="Interests" items={tagsFrom(detail.interest_tags, detail.hobby_tags, detail.interests)} />
                <DetailList title="Allergies & dislikes" items={tagsFrom(detail.allergies, detail.dislike_tags, detail.dislikes)} />
                <DetailList title="Occasions" items={occasionItems} renderItem={item => `${item.occasion_type || "Occasion"} · ${item.occasion_date || item.next_occurrence || ""}`} />
                <DetailLine label="Memory notes" value={detail.notes} />
              </>
            )}
            {type === "occasion" && (
              <>
                <DetailLine label="Person" value={detail.person_name} />
                <DetailLine label="Occasion" value={(detail.occasion_type || "").replaceAll("_", " ")} />
                <DetailLine label="Date" value={detail.occasion_date} />
              </>
            )}
            {type === "trip" && (
              <>
                <DetailLine label="Destination" value={detail.destination} />
                <DetailLine label="Style" value={detail.trip_style} />
                <DetailLine label="Duration" value={detail.duration ? `${detail.duration} days` : ""} />
                <DetailLine label="Travelers" value={detail.travelers} />
                <DetailLine label="Notes" value={detail.notes} />
                <DetailList title="Packing" items={packingItems} renderItem={item => typeof item === "string" ? item : [item.name || item.item, item.quantity].filter(Boolean).join(" · ")} />
                <DetailList title="Activities" items={activityItems} renderItem={item => typeof item === "string" ? item : [item.title || item.name, item.time || item.day].filter(Boolean).join(" · ")} />
              </>
            )}
            {type === "event" && (
              <>
                <DetailLine label="Event" value={(detail.event_type || detail.occasion_type || "").replaceAll("_", " ")} />
                <DetailLine label="Date" value={detail.event_date || detail.occasion_date} />
                <DetailLine label="End date" value={detail.end_date} />
                <DetailLine label="Location" value={detail.location || detail.destination} />
                <DetailLine label="Guests" value={detail.guest_count || detail.rsvp_in || detail.rsvp_count} />
                <DetailLine label="Notes" value={detail.notes} />
              </>
            )}
            {type === "pet" && (
              <section className="hm-shared-pet-view">
                <div className="hm-shared-pet-identity">{detail.photo_url ? <img src={detail.photo_url} alt={detail.name} /> : <i>🐾</i>}<div><h2>{detail.name}</h2><p>{[detail.species, detail.breed, detail.color_markings].filter(Boolean).join(" · ")}</p></div></div>
                <div className="hm-shared-pet-facts"><DetailLine label="Feeding" value={detail.feeding_notes} /><DetailLine label="Caregiver notes" value={detail.caregiver_notes} /><DetailLine label="Microchip" value={detail.microchip_number} /><DetailLine label="Allergies" value={detail.allergy_notes} /><DetailLine label="Medical conditions" value={detail.medical_conditions} /><DetailLine label="Medication" value={detail.medication_notes} /><DetailLine label="Insurance" value={detail.insurance_provider ? `${detail.insurance_provider}${detail.insurance_policy_number ? ` · ${detail.insurance_policy_number}` : ""}` : null} /></div>
                {(detail.primary_vet_name || detail.emergency_vet_name) && <section><h3>Veterinarian contacts</h3>{detail.primary_vet_name && <a href={detail.primary_vet_phone ? `tel:${detail.primary_vet_phone}` : undefined}><b>{detail.primary_vet_name}</b><span>{detail.primary_vet_phone}</span></a>}{detail.emergency_vet_name && <a href={detail.emergency_vet_phone ? `tel:${detail.emergency_vet_phone}` : undefined}><b>{detail.emergency_vet_name}</b><span>{detail.emergency_vet_phone}</span></a>}</section>}
                <section><h3>Daily care</h3>{detail.routines?.length ? detail.routines.map((routine) => <button type="button" key={routine.id} className={routine.completed ? "is-complete" : ""} disabled={!canHelpWithPetCare || saving} onClick={() => toggleSharedPetRoutine(routine)}><span><b>{routine.title}</b><small>{routine.time_of_day}{routine.dosage ? ` · ${routine.dosage}` : ""}{routine.instructions ? ` · ${routine.instructions}` : ""}{routine.completed_by_name ? ` · Done by ${routine.completed_by_name}` : ""}</small></span><strong>{routine.completed ? "✓ Done" : canHelpWithPetCare ? "Mark done" : "Pending"}</strong></button>) : <p>No daily routines shared.</p>}</section>
                {!!detail.upcoming_care?.length && <DetailList title="Upcoming care" items={detail.upcoming_care} renderItem={(item) => `${item.title}${item.next_due ? ` · ${fullDate(parseDate(item.next_due))}` : ""}`} />}
                {!!detail.documents?.length && <DetailList title="Linked documents" items={detail.documents} renderItem={(item) => `${item.name}${item.category ? ` · ${item.category}` : ""}`} />}
              </section>
            )}
            {!["recipe", "meal_plan", "pantry", "shopping_list", "person", "occasion", "trip", "event", "pet"].includes(type) && (
              <>
                <DetailLine label="Type" value={type} />
                <DetailLine label="Summary" value={share.summary || detail.description || detail.notes} />
              </>
            )}
          </section>}

          {type === "meal_plan" && !mealPlanSuccess && !isOwnerViewer && (hasMealAllergyConflict || hasMissingFoodPreferences || preferenceCheck?.status === "ok") && (
            <section className={`hm-shared-safety-note${hasMealAllergyConflict ? " is-blocked" : preferenceCheck?.status === "ok" ? " is-ok" : ""}`}>
              <strong>
                {hasMealAllergyConflict
                  ? `${hardConflicts.length} meal${hardConflicts.length === 1 ? "" : "s"} match blocked allergens`
                  : hasMissingFoodPreferences
                    ? "Food preferences not set"
                    : "Checked against your preferences"}
              </strong>
              <span>
                {hasMealAllergyConflict
                  ? "HouseMind will not copy unsafe meals into your plan."
                  : hasMissingFoodPreferences
                    ? "Set allergies first so HouseMind can check shared meals before copying."
                    : "No blocked allergens found for your account."}
              </span>
              {hasMissingFoodPreferences && (
                <button type="button" onClick={() => navigate("/app/food?tab=preferences", { state: { foodTab: "preferences" } })}>
                  Set preferences
                </button>
              )}
            </section>
          )}

          {!mealPlanSuccess && showActionButton && (
            <footer className="hm-shared-meal-footer">
              <button type="button" className="hm-shared-footer-primary" onClick={runPrimaryAction} disabled={saving || saved}>
                {primaryActionLabel()}
              </button>
              <button type="button" className="hm-shared-footer-link" onClick={() => navigate("/app/family")}>
                Back to family sharing
              </button>
            </footer>
          )}

          {!mealPlanSuccess && !showActionButton && !ownerNote && (
            <footer className="hm-shared-meal-footer hm-shared-meal-footer--link-only">
              <button type="button" className="hm-shared-footer-link" onClick={() => navigate("/app/family")}>
                Back to family sharing
              </button>
            </footer>
          )}

          {ownerNote && !mealPlanSuccess && (
            <footer className="hm-shared-owner-note">
              <strong>{ownerNote.title}</strong>
              <span>{ownerNote.body}</span>
              <button type="button" onClick={() => navigate("/app/family")}>
                {ownerNote.button}
              </button>
            </footer>
          )}

          {type === "meal_plan" && mealPlanSuccess && (
            <SharedMealPlanSuccess
              success={mealPlanSuccess}
              onOpenMealPlan={() => {
                const tab = mealPlanSuccess?.mode === "save_recipes" ? "recipes" : "meal-plan";
                navigate(`/app/food?tab=${tab}`, { state: { foodTab: tab } });
              }}
              onBack={() => navigate("/app/family")}
            />
          )}

          {mealActionSheet && (
            <div className="hm-sheet-overlay hm-shared-sheet-overlay" role="presentation" onClick={closeMealActionSheetWithSlide}>
      <BackButtonTrap onClose={closeMealActionSheetWithSlide} />
              <section
                className={`hm-action-sheet hm-family-standard-sheet hm-shared-decision-sheet${mealSheetDragging ? " is-dragging" : ""}`}
                style={{ transform: mealSheetOffset ? `translateY(${mealSheetOffset}px)` : undefined }}
                role="dialog"
                aria-modal="true"
                aria-label="Shared meal actions"
                onClick={event => event.stopPropagation()}
              >
                <div className="hm-shared-sheet-top-row">
                  <button
                    type="button"
                    className="hm-shared-decision-handle"
                    onClick={closeMealActionSheetWithSlide}
                    onPointerDown={event => {
                      if (event.pointerType === "mouse" && event.button !== 0) return;
                      event.currentTarget.setPointerCapture?.(event.pointerId);
                      beginMealSheetDrag(event.clientY);
                    }}
                    onPointerMove={event => updateMealSheetDrag(event.clientY, event)}
                    onPointerUp={event => {
                      event.currentTarget.releasePointerCapture?.(event.pointerId);
                      finishMealSheetDrag();
                    }}
                    onPointerCancel={event => {
                      event.currentTarget.releasePointerCapture?.(event.pointerId);
                      finishMealSheetDrag();
                    }}
                    aria-label="Close meal actions"
                  />
                  <button type="button" className="hm-sheet-top-close hm-shared-sheet-top-close" onClick={closeMealActionSheet} aria-label="Close meal actions">×</button>
                </div>
                <SheetLogoRow title="Meal actions" onClose={closeMealActionSheet} closeLabel="Close meal actions" showClose={false} />
                {(() => {
                  const day = mealActionSheet.day || {};
                  const key = sharedMealKey(day);
                  const parts = mealTitleParts(day);
                  const allergyMatches = unsafeMealMatches(day);
                  const isUnsafe = allergyMatches.length > 0;
                  return (
                    <>
                      <h2>{parts.title || day.meal_name || "Shared meal"}</h2>
                      <p>{day.day_label || "Day"} · Choose what to do with this shared meal.</p>
                      {isUnsafe && <p className="hm-shared-decision-warning">Blocked allergen: {allergyMatches.slice(0, 3).join(", ")}</p>}
                      <button type="button" className="hm-shared-decision-option" onClick={() => saveMealDaysAsRecipes([key])} disabled={saving || isUnsafe}>
                        <span className="hm-shared-decision-icon">☆</span>
                        <span>
                          <strong>Save to recipes</strong>
                          <small>Keeps this idea without changing your schedule.</small>
                        </span>
                        <span aria-hidden="true">›</span>
                      </button>
                      <button type="button" className="hm-shared-decision-option" onClick={() => swapMealDaysIntoPlan([key])} disabled={saving || isUnsafe}>
                        <span className="hm-shared-decision-icon">☷</span>
                        <span>
                          <strong>Swap into this day</strong>
                          <small>Replaces your meal on this day with the shared one.</small>
                        </span>
                        <span aria-hidden="true">›</span>
                      </button>
                    </>
                  );
                })()}
              </section>
            </div>
          )}

          {mealPlanDecision && (
            <div className="hm-shared-sheet-overlay" role="presentation" onClick={closeMealPlanDecision}>
      <BackButtonTrap onClose={closeMealPlanDecision} />
              <section className="hm-shared-decision-sheet" role="dialog" aria-modal="true" aria-label="Add shared meal plan" onClick={event => event.stopPropagation()}>
                <button
                  type="button"
                  className="hm-shared-decision-handle"
                  onPointerDown={startDecisionHandleDrag}
                  onPointerMove={moveDecisionHandleDrag}
                  onPointerUp={endDecisionHandleDrag}
                  onPointerCancel={() => { decisionHandleDragRef.current = { active: false, startY: 0, moved: false }; }}
                  onClick={closeMealPlanDecision}
                  aria-label="Close add meal plan options"
                />
                <button type="button" className="hm-shared-decision-close" onClick={closeMealPlanDecision} aria-label="Close add meal plan options">×</button>
                {choosingDays ? (
                  <>
                    <h2>Choose each day</h2>
                    <p>Keep your meal or use the shared one for each day.</p>
                    <div className="hm-shared-day-choices">
                      {(mealPlanDecision.days || []).map(day => (
                        <div key={day.day_index} className="hm-shared-day-choice">
                          <strong>{day.day_label}</strong>
                          <div>
                            <button
                              type="button"
                              className={!selectedDays[day.day_index] ? "is-active" : ""}
                              onClick={() => toggleSharedDay(day.day_index, false)}
                            >
                              Keep mine
                              <span>{day.mine?.meal_name || "Empty"}</span>
                            </button>
                            <button
                              type="button"
                              className={selectedDays[day.day_index] ? "is-active" : ""}
                              onClick={() => toggleSharedDay(day.day_index, true)}
                              disabled={day.is_unsafe}
                            >
                              {day.is_unsafe ? "Blocked" : "Use shared"}
                              <span>{day.is_unsafe ? `Allergy: ${(day.allergy_matches || []).slice(0, 2).join(", ")}` : day.shared?.meal_name || "Empty"}</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="hm-shared-decision-primary" onClick={submitChosenDays} disabled={saving}>
                      Add selected days
                    </button>
                    <button type="button" className="hm-shared-decision-secondary" onClick={() => setChoosingDays(false)}>
                      Back
                    </button>
                  </>
                ) : (
                  <>
                    <h2>{mealPlanDecision.status === "allergy_conflict" ? "Allergy conflict found" : mealPlanDecision.status === "full" ? "Your week is full" : "You already have meals this week"}</h2>
                    <p>
                      {mealPlanDecision.status === "allergy_conflict"
                        ? "Some shared meals match allergens blocked in your preferences. You can add only the safe meals."
                        : `You have ${mealPlanDecision.filled_count || 0} day${mealPlanDecision.filled_count === 1 ? "" : "s"} planned. How would you like to add ${ownerFirstName(ownerName)}'s meals?`}
                    </p>
                    {mealPlanDecision.status === "allergy_conflict" ? (
                      <button type="button" className="hm-shared-decision-option" onClick={() => addMealPlan("add_safe_only")} disabled={saving || mealPlanDecision.empty_count === 0}>
                        <span className="hm-shared-decision-icon">＋</span>
                        <span>
                          <strong>Add safe meals only</strong>
                          <small>Skips meals with blocked allergens.</small>
                        </span>
                        <span aria-hidden="true">›</span>
                      </button>
                    ) : (
                      <button type="button" className="hm-shared-decision-option" onClick={() => addMealPlan("fill_empty")} disabled={saving || mealPlanDecision.empty_count === 0}>
                        <span className="hm-shared-decision-icon">＋</span>
                        <span>
                          <strong>Fill empty days only</strong>
                          <small>Adds shared meals to your {mealPlanDecision.empty_count || 0} empty day{mealPlanDecision.empty_count === 1 ? "" : "s"}.</small>
                        </span>
                        <span aria-hidden="true">›</span>
                      </button>
                    )}
                    <button type="button" className="hm-shared-decision-option" onClick={() => setChoosingDays(true)}>
                      <span className="hm-shared-decision-icon">☷</span>
                      <span>
                        <strong>Let me choose day by day</strong>
                        <small>Pick which meals to keep and which to replace.</small>
                      </span>
                      <span aria-hidden="true">›</span>
                    </button>
                    <button type="button" className="hm-shared-decision-quiet" onClick={closeMealPlanDecision}>
                      Cancel
                    </button>
                  </>
                )}
              </section>
            </div>
          )}

          {recipeSheetOpen && (
            <SharedRecipeCookSheet
              recipe={detail}
              ingredients={ingredients}
              instructions={instructions}
              saving={saving}
              onCook={cookSharedRecipe}
              onClose={() => setRecipeSheetOpen(false)}
            />
          )}

          {itemsDestinationOpen && (
            <SharedItemsDestinationSheet
              type={type}
              ownerName={ownerName}
              saving={saving}
              onAddPantry={() => addPantry()}
              onAddShopping={addShoppingItems}
              onClose={() => setItemsDestinationOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
